'use client';

import { useCallback, useState } from 'react';
import { number, plural } from '@frameio/i18n';
import { AlertDialog, AlertDialogSecondaryAction, Text } from '@frameio/vapor';
import {
  parseRateLimitError,
  type RateLimitErrorInput,
} from '../utils/parseRateLimitError';
import TEXT from './generated/TEXT/useFireflyRateLimit';
import { useFireflyErrorToast } from './useFireflyErrorToast';

// Copy is shared across the three actions and pending final wording (AIINT-123);
// keeping it in one block makes that swap a single edit.
TEXT.DEFINE({
  /** Toast shown when the account has no generations left and no reset date is known. */
  limitReachedToast:
    "You've hit your generation limit for now — check back later.",
  /** Toast shown when the account has no generations left, dated with the reset day. */
  limitReachedToastDated: ({ date }: { date: string }): string =>
    `You've hit your generation limit — try again after ${date}.`,
  /** Title of the "reduce your selection" dialog (some remaining, but not enough). */
  partialLimitTitle: 'Not enough generations remaining',
  /** Body explaining how many remain vs. how many the selection needs. */
  partialLimitBody: ({
    remaining,
    attempted,
  }: {
    remaining: number;
    attempted: number;
  }): string =>
    `You have ${plural(
      remaining,
      `${number(remaining)} generations`,
      `${number(remaining)} generation`
    )} remaining, but this selection would use ${number(
      attempted
    )}. Reduce your selection and try again.`,
  /** Dismiss button for the "reduce your selection" dialog. */
  partialLimitDismiss: 'Got it',
});

const resetDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  day: 'numeric',
});

function formatResetDate(date: Date): string {
  return resetDateFormatter.format(date);
}

type PartialLimitState = { remaining: number; attempted: number } | null;

/**
 * Centralized, reactive rate-limit handling for the Firefly actions. Callers run
 * `handleRateLimitError` in their error branch: it returns `true` and renders the
 * appropriate rich UI when the backend emits the `TOO_MANY_REQUESTS` contract,
 * and `false` otherwise so the caller can fall back to its generic error toast.
 */
export function useFireflyRateLimit() {
  const showErrorToast = useFireflyErrorToast();
  const [partialLimitState, setPartialLimitState] =
    useState<PartialLimitState>(null);

  const handleRateLimitError = useCallback(
    (input: RateLimitErrorInput): boolean => {
      const info = parseRateLimitError(input);
      if (!info) return false;

      if (info.remaining <= 0) {
        showErrorToast(
          info.resetsAt
            ? TEXT.limitReachedToastDated({
                date: formatResetDate(info.resetsAt),
              })
            : TEXT.limitReachedToast
        );
      } else {
        setPartialLimitState({
          remaining: info.remaining,
          attempted: info.attempted,
        });
      }
      return true;
    },
    [showErrorToast]
  );

  const rateLimitDialog = partialLimitState ? (
    <AlertDialog
      actions={
        <AlertDialogSecondaryAction>
          {TEXT.partialLimitDismiss}
        </AlertDialogSecondaryAction>
      }
      details={
        <Text
          color="body"
          fontWeight="regular"
          textAlign="center"
          variant="body"
        >
          {TEXT.partialLimitBody({
            remaining: partialLimitState.remaining,
            attempted: partialLimitState.attempted,
          })}
        </Text>
      }
      isShowing={true}
      onRequestClose={() => setPartialLimitState(null)}
      title={TEXT.partialLimitTitle}
      variant="notice"
    />
  ) : null;

  return { handleRateLimitError, rateLimitDialog };
}
